import { useEffect, useRef, useState } from "react"
import { ArrowLeft } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormDataAATCC127 {
  ROW_ID?: string
  numero_solicitud: string
  fecha: string
  hora: string
  temperatura: string
  hr: string
  id_termohigrometro: string
  tipo_material: string
  tipo_agua: string
  temp_agua: string
  cara_delantera: string
  no1: string
  no2: string
  no3: string
  promedio: string
  observaciones: string
  mostrarObservaciones: boolean
  desv_metodo: string
  realizo: string
  superviso: string
}

interface Props {
  mode: "create" | "view" | "edit"
  initialData?: any
  onSave?: (data: FormDataAATCC127) => void
  onCancel?: () => void
  realizoUser?: string
  onRefreshBackend?: () => void
  rol?: string
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

const DEFAULTS: FormDataAATCC127 = {
  numero_solicitud: '', fecha: '', hora: '', temperatura: '', hr: '',
  id_termohigrometro: '', tipo_material: '', tipo_agua: '', temp_agua: '', cara_delantera: '',
  no1: '', no2: '', no3: '', promedio: '',
  observaciones: '', mostrarObservaciones: false, desv_metodo: '', realizo: '', superviso: '',
}

function roundHydro(n: number): string {
  return (Math.round(n * 100 + Number.EPSILON) / 100).toFixed(2)
}

const TD = "border border-gray-500 px-1 py-0.5 align-top text-xs"

export function AATCCForm({ mode, initialData = {}, onSave, onCancel, realizoUser, onRefreshBackend, rol, pedidoRowId, onAutosave }: Props) {
  const globalRol = useUserRol()
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol
  const [formData, setFormData] = useState<FormDataAATCC127>({
    ...DEFAULTS,
    realizo: mode === 'create' && realizoUser ? realizoUser : '',
    ...initialData,
    mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
  })
  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false)
  const [rechazoLoading, setRechazoLoading] = useState(false)

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData({ ...DEFAULTS, realizo: realizoUser || '', ...initialData, mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true })
    setEditMode(false)
  }, [initialData])

  const aceptadoRaw = initialData?.ACEPTADO ?? initialData?.aceptado
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : ''
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado'
  const isReadOnly = mode === 'view' && !editMode
  const [autosaveRowId, setAutosaveRowId] = useState<string>(formData?.ROW_ID || initialData?.ROW_ID || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>(initialData?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId

  const set = (field: keyof FormDataAATCC127, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value }
      const vals = [next.no1, next.no2, next.no3].map(v => parseFloat(v)).filter(n => !isNaN(n))
      if (vals.length > 0) {
        const prom = vals.reduce((a, b) => a + b, 0) / vals.length
        next.promedio = roundHydro(prom)
      }
      return next
    })
  }

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData
    return rest
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'AATCC TM127-2017(2018) TEST METHOD FOR WATER RESISTANCE: HYDROSTATIC PRESSURE',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) onSave(formData)
    } finally {
      setGuardando(false)
    }
  }

  const handleGuardarEdicion = () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de AATCC-TM127? Esta acción no se puede deshacer.')) return
    if (onSave) onSave({ ...formData, ROW_ID: rowId, _EDIT_MODE: true } as any)
  }

  const handleAccion = async () => {
    if (!window.confirm('Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba AATCC-TM127?')) return
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'aceptado', undefined, undefined, nomId || undefined)
      toast.success('Prueba aceptada correctamente.')
      onRefreshBackend?.()
    } catch (e: any) { toast.error(e.message) }
  }

  const inputCls = (extra?: string) =>
    `h-6 text-xs px-1 ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''} ${extra ?? ''}`

  return (
    <>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-4 space-y-3">

        {/* ── HEADER TABLE ── */}
        <table className="w-full text-xs border-collapse border-2 border-gray-700">
          <tbody>
            {/* Row 1: Número de solicitud */}
            <tr>
              <td colSpan={5} className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">NÚMERO DE SOLICITUD:</span>
                  <Input className={inputCls('w-48')}
                    value={formData.numero_solicitud} readOnly={isReadOnly}
                    onChange={e => set('numero_solicitud', e.target.value)} />
                </div>
              </td>
            </tr>

            {/* Row 2: Título + MET */}
            <tr>
              <td colSpan={4} className={`${TD} text-center font-bold leading-snug py-1`}>
                AATCC TM127-2017(2018) e TEST METHOD FOR WATER RESISTANCE: HYDROSTATIC PRESSURE
              </td>
              <td className={`${TD} text-center font-bold whitespace-nowrap`}>MET-7.2-21</td>
            </tr>

            {/* Row 3: Fecha / Hora / °C / %H.R. / ID Termohig */}
            <tr>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Fecha:</div>
                <Input type="date" className={inputCls('w-full')}
                  value={formData.fecha} readOnly={isReadOnly}
                  onChange={e => set('fecha', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Hora:</div>
                <Input type="time" className={inputCls('w-full')}
                  value={formData.hora} readOnly={isReadOnly}
                  onChange={e => { if (e.target.value) set('hora', e.target.value) }} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">°C:</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.temperatura} readOnly={isReadOnly}
                  onChange={e => set('temperatura', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">%H.R.:</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.hr} readOnly={isReadOnly}
                  onChange={e => set('hr', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">ID de termohigrómetro:</div>
                {isReadOnly ? (
                  <Input value={formData.id_termohigrometro} readOnly className={inputCls('w-full')} />
                ) : (
                  <Select value={formData.id_termohigrometro} onValueChange={v => set('id_termohigrometro', v)}>
                    <SelectTrigger className="h-6 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="E-59">E-59</SelectItem>
                      <SelectItem value="E-57">E-57</SelectItem>
                      <SelectItem value="E-56">E-56</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </td>
            </tr>

            {/* Row 4: Tipo material / Tipo agua / Temp agua / Cara delantera */}
            <tr>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Tipo de material:</div>
                <Input className={inputCls('w-full')}
                  value={formData.tipo_material} readOnly={isReadOnly}
                  onChange={e => set('tipo_material', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Tipo de agua:</div>
                <Input className={inputCls('w-full')}
                  value={formData.tipo_agua} readOnly={isReadOnly}
                  onChange={e => set('tipo_agua', e.target.value)} />
              </td>
              <td colSpan={2} className={TD}>
                <div className="font-medium whitespace-nowrap">Temperatura del agua (°C):</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.temp_agua} readOnly={isReadOnly}
                  onChange={e => set('temp_agua', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Cara delantera probada</div>
                <Input className={inputCls('w-full')}
                  value={formData.cara_delantera} readOnly={isReadOnly}
                  onChange={e => set('cara_delantera', e.target.value)} />
              </td>
            </tr>

            {/* Row 5: Campos fijos */}
            <tr>
              <td colSpan={2} className={TD}>
                <span className="font-medium">Gradiente:</span>{' '}
                <span className="font-bold">60 mbar/min</span>
              </td>
              <td colSpan={3} className={TD}>
                <span className="font-medium">Tipo de equipo:</span>{' '}
                <span className="font-bold">Equipo de cabeza hidrostática (opción 2)</span>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── MAIN SECTION: tabla presión (izq) + Realizó/Supervisó (der) ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              {/* Left: measurement table */}
              <td className="border border-gray-500 p-2 align-top w-[55%]">
                <table className="w-full text-xs border-collapse border border-gray-500" style={{ width: '60%', margin: '0 auto' }}>
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-500 p-1 text-center w-14">No.</th>
                      <th className="border border-gray-500 p-1 text-center">Presión hidrostática</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1,2,3].map(i => (
                      <tr key={i}>
                        <td className="border border-gray-500 p-1 text-center">{i}</td>
                        <td className="border border-gray-500 p-0.5">
                          <Input className="h-5 text-xs px-1 border-0 shadow-none"
                            value={(formData as any)[`no${i}`] || ''} readOnly={isReadOnly}
                            onChange={e => set(`no${i}` as keyof FormDataAATCC127, e.target.value)} />
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="border border-gray-500 p-1">Promedio</td>
                      <td className="border border-gray-500 p-1 text-center">
                        {formData.promedio ? `${formData.promedio} cm/H₂O` : <span className="text-gray-400">cm/H₂O</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>

              {/* Right: Realizó / Supervisó */}
              <td className="border border-gray-500 p-2 align-top w-[45%]">
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-medium mb-1">Realizó:</div>
                    <Input value={formData.realizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                  <div>
                    <div className="text-xs font-medium mb-1">Supervisó:</div>
                    <Input value={formData.superviso} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── FOOTER ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              <td className="border border-gray-500 px-2 py-1">
                <div className="font-medium mb-1 flex items-center gap-2">
                  Observaciones:
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    Mostrar en Reporte Cliente
                    <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                      onCheckedChange={(checked) => set('mostrarObservaciones', checked)} />
                  </span>
                </div>
                <Textarea value={formData.observaciones} readOnly={isReadOnly} rows={2}
                  className={`text-xs ${isReadOnly ? 'border-0 shadow-none bg-transparent resize-none' : ''}`}
                  onChange={e => set('observaciones', e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Desviación al método */}
        <div>
          <label className="text-xs font-medium block mb-1">Desviación al método</label>
          <Textarea value={formData.desv_metodo} readOnly={isReadOnly} rows={2}
            className={`text-xs ${isReadOnly ? 'bg-muted-foreground/10 cursor-not-allowed' : ''}`}
            onChange={e => set('desv_metodo', e.target.value)} />
        </div>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={handleBackFlush}>Cancelar</Button>}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={handleAccion}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar Formulario'}</Button>
          )}
        </div>

      </CardContent>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={async (razon: string) => {
          setRechazoLoading(true)
          try {
            // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
            // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
            if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
            while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
            await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId || undefined)
            toast.success('Prueba rechazada')
            setShowRechazoModal(false)
            onRefreshBackend?.()
          } catch (e: any) { toast.error(e.message) }
          finally { setRechazoLoading(false) }
        }}
      />
    </Card>
    </>
  )
}
